import React from "react";
import { Trophy, Lock, Sparkles } from "lucide-react";

/** Map backend `bg` token → CSS hex. */
const BG_MAP = {
    "white": "#fff",
    "#CCFF00": "#CCFF00",
    "#FF00FF": "#FF00FF",
    "black": "#000",
};
const FG_FOR = (bg) => (bg === "black" || bg === "#FF00FF" ? "#fff" : "#000");

/**
 * Tiered referral reward visualiser.
 *
 *  Props:
 *    tier {                     ← exact shape from /api/waitlist/me .tier
 *      all_tiers: [{ name, threshold, perk, bg }],
 *      current:   { name, threshold, perk, bg },
 *      next:      { name, threshold, perk, bg } | null,
 *      to_next:   number,
 *    }
 *    referralsCount (number)    ← how many invites the user has so far
 */
export const TierProgress = ({ tier, referralsCount = 0 }) => {
    if (!tier?.all_tiers?.length) return null;
    const { all_tiers: tiers, current, next: nextTier, to_next: toNext } = tier;

    // Bar fill: % between current.threshold and next.threshold. 100% once topped out.
    const fill = (() => {
        if (!nextTier) return 100;
        const span = nextTier.threshold - current.threshold || 1;
        const done = referralsCount - current.threshold;
        return Math.max(0, Math.min(100, (done / span) * 100));
    })();

    return (
        <div data-testid="tier-progress" className="border-2 border-black bg-white">
            <div className="border-b-2 border-black px-3 py-2 flex items-center justify-between bg-black text-[#CCFF00]">
                <span className="font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5">
                    <Trophy className="w-3 h-3" /> Tier
                </span>
                <span
                    data-testid="tier-current"
                    className="font-display font-black text-sm uppercase tracking-tighter"
                    style={{ color: current.bg === "white" ? "#fff" : current.bg }}
                >
                    {current.name}
                </span>
            </div>

            {/* Bar */}
            <div className="px-3 pt-3 pb-2">
                <div className="relative h-3 border-2 border-black bg-white">
                    <div
                        data-testid="tier-progress-fill"
                        className="absolute inset-y-0 left-0 transition-all duration-500"
                        style={{ width: `${fill}%`, background: "#FF00FF" }}
                    />
                </div>
                <div className="flex justify-between mt-1 font-mono text-[10px] uppercase tracking-widest">
                    <span>{referralsCount} invited</span>
                    {nextTier ? (
                        <span data-testid="tier-to-next">{toNext} to <strong>{nextTier.name}</strong></span>
                    ) : (
                        <span data-testid="tier-maxed" className="text-[#FF00FF] font-bold inline-flex items-center gap-1">
                            <Sparkles className="w-3 h-3" /> Maxed out
                        </span>
                    )}
                </div>
            </div>

            {/* Tier ladder */}
            <ul className="divide-y-2 divide-black border-t-2 border-black">
                {tiers.map((t, i) => {
                    const unlocked = referralsCount >= t.threshold;
                    const bg = unlocked ? (BG_MAP[t.bg] || t.bg) : "#fff";
                    const fg = unlocked ? FG_FOR(t.bg) : "#999";
                    return (
                        <li
                            key={t.name}
                            data-testid={`tier-row-${i}`}
                            className="px-3 py-2 flex items-center gap-3"
                            style={{ background: bg, color: fg }}
                        >
                            <div className="font-display font-black text-xs uppercase tracking-tight w-20 truncate">
                                {t.name}
                            </div>
                            <div className="font-mono text-[10px] flex-1 leading-tight">
                                {t.perk}
                            </div>
                            <div className="font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-1">
                                {unlocked ? (
                                    <>✓ {t.threshold === 0 ? "DONE" : `${t.threshold}+`}</>
                                ) : (
                                    <><Lock className="w-3 h-3" /> {t.threshold}</>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};
